import type { User as FirebaseUser } from 'firebase/auth';
import m from 'mithril';
import '@awesome.me/webawesome/dist/components/icon/icon.js';

import { logIn, logOut, resetPassword } from '../services/auth';
import { getAuthState } from '../services/authStore';
import { captureException } from '../services/sentry';
import {
  hasNoErrors,
  validateLogin,
  validateReset,
  type LoginValues,
} from '../services/forms';
import { Button } from '../components/Button';
import { TextField } from '../components/FormControls';
import { Loading } from '../components/Loading';
import { Type } from '../components/Typography';

/**
 * Login route attrs.
 */
export interface LoginAttrs extends m.Attributes {
  /** Optional encoded redirect segment. */
  redirect?: string;
}

const loginErrorMessage = (error: unknown): string => {
  const message = error instanceof Error ? error.message : String(error);
  if (
    message.includes('password is invalid')
    || message.includes('no user record')
    || message.includes('auth/invalid-credential')
  ) {
    return 'Uh oh! Incorrect email/password combination...';
  }
  return 'Uh oh! Something went wrong...';
};

const decodedRedirect = (redirect: string | undefined): string => {
  return decodeURIComponent(redirect ?? 'account').replace(/^\/+/, '');
};

const continueAfterLogin = (user: FirebaseUser, redirect: string | undefined): void => {
  if (!user.emailVerified) {
    m.route.set('/verifyemail');
    return;
  }
  m.route.set('/' + decodedRedirect(redirect));
};

/**
 * Login and password reset page.
 */
export const Login: m.ClosureComponent<LoginAttrs> = () => {
  const state = {
    values: {
      email: '',
      password: '',
    } satisfies LoginValues,
    resetEmail: '',
    resetting: false,
    sentReset: false,
    loading: false,
    error: '',
    errors: {} as Partial<Record<keyof LoginValues | 'email', string>>,
  };

  const submitLogin = (redirect: string | undefined): void => {
    state.errors = validateLogin(state.values);
    state.error = '';
    if (!hasNoErrors(state.errors)) {
      return;
    }

    state.loading = true;
    void logIn(state.values.email, state.values.password)
      .then((user) => {
        state.loading = false;
        continueAfterLogin(user, redirect);
      })
      .catch((error: unknown) => {
        captureException(error);
        state.loading = false;
        state.error = loginErrorMessage(error);
        m.redraw();
      });
  };

  const submitReset = (): void => {
    const errors = validateReset(state.resetEmail);
    state.errors = errors;
    state.error = '';
    state.sentReset = false;
    if (errors.email) {
      return;
    }

    state.loading = true;
    void resetPassword(state.resetEmail)
      .then(() => {
        state.loading = false;
        state.sentReset = true;
        m.redraw();
      })
      .catch((error: unknown) => {
        captureException(error);
        state.loading = false;
        state.error = loginErrorMessage(error);
        m.redraw();
      });
  };

  return {
    view: ({ attrs }) => {
      const auth = getAuthState();
      const isLoading = state.loading || auth.authLoading;

      return m('section', { class: 'page page--narrow auth-page' }, [
        m('div', { class: 'form-panel' }, [
          isLoading ? m(Loading, { large: true }) : auth.firebaseUser ? [
            m(Type, { size: 'subheading' }, 'Logged in as'),
            m(Type, { size: 'heading' }, auth.firebaseUser.email ?? 'MeshOS user'),
            m('div', { class: 'form-actions form-actions--stacked' }, [
              m(Button, {
                full: true,
                onclick: (event) => {
                  event.redraw = false;
                  m.route.set('/' + decodedRedirect(attrs.redirect));
                },
              }, 'Continue'),
              m(Button, {
                full: true,
                appearance: 'outlined',
                variant: 'neutral',
                onclick: (event) => {
                  event.redraw = false;
                  state.loading = true;
                  void logOut().finally(() => {
                    state.loading = false;
                    m.redraw();
                  });
                },
              }, 'Sign Out'),
            ]),
          ] : state.resetting ? [
            m(Type, { size: 'heading' }, 'Reset Password'),
            state.error ? m(Type, { className: 'form-error' }, state.error) : null,
            state.sentReset
              ? m(Type, { className: 'form-success' },
                'Reset email sent. Please check your email and follow the instructions to reset your password.')
              : null,
            m('form', {
              class: 'stacked-form',
              onsubmit: (event: SubmitEvent) => {
                event.preventDefault();
                submitReset();
              },
            }, [
              m(TextField, {
                label: 'Email',
                name: 'email',
                type: 'email',
                placeholder: 'Email',
                startIcon: 'mail',
                value: state.resetEmail,
                hint: state.errors.email,
                onInput: (value) => {
                  state.resetEmail = value;
                },
              }),
              m(Button, { type: 'submit', full: true }, 'Send Reset Email'),
              m(Button, {
                type: 'button',
                full: true,
                appearance: 'outlined',
                variant: 'neutral',
                onclick: (event) => {
                  event.redraw = false;
                  state.sentReset = false;
                  state.error = '';
                  state.resetting = false;
                },
              }, 'Back to Login'),
            ]),
          ] : [
            m(Type, { size: 'heading' }, 'Login to MeshOS'),
            state.error ? m(Type, { className: 'form-error', id: 'error' }, state.error) : null,
            m('form', {
              class: 'stacked-form',
              onsubmit: (event: SubmitEvent) => {
                event.preventDefault();
                submitLogin(attrs.redirect);
              },
            }, [
              m(TextField, {
                label: 'Email',
                name: 'email',
                type: 'email',
                placeholder: 'Email',
                startIcon: 'mail',
                value: state.values.email,
                hint: state.errors.email,
                onInput: (value) => {
                  state.values.email = value;
                },
              }),
              m(TextField, {
                label: 'Password',
                name: 'password',
                type: 'password',
                placeholder: 'Password',
                startIcon: 'lock',
                value: state.values.password,
                hint: state.errors.password,
                onInput: (value) => {
                  state.values.password = value;
                },
              }),
              m('div', { class: 'form-actions form-actions--stacked' }, [
                m(Button, { type: 'submit', full: true }, [
                  'Login',
                  m('wa-icon', { slot: 'end', library: 'material', name: 'arrow_forward' }),
                ]),
                m(Button, {
                  type: 'button',
                  full: true,
                  appearance: 'outlined',
                  variant: 'brand',
                  onclick: (event) => {
                    event.redraw = false;
                    state.error = '';
                    state.sentReset = false;
                    state.resetting = true;
                  },
                }, 'Forgot Password'),
              ]),
            ]),
          ],
        ]),
      ]);
    },
  };
};
